import React, { useState, useEffect, useCallback } from "react";
import { Button, CircularProgress, Backdrop, Typography, Breadcrumbs, Link } from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { Form } from "react-bootstrap";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import Swal from "sweetalert2";

import LoginHeader from "./components/LoginHeader.js";
import SideNavigation from "./components/SideNavigation.js";
import AADHeader from "./components/AADHeader.js";
import { WASTE_TYPES, DISPOSAL_METHODS, ANY_DISPOSAL_METHOD, GWP_BASES } from "./constants/wasteTaxonomy";

const emptyForm = { partnerName: "", wasteType: "", disposalMethod: ANY_DISPOSAL_METHOD, factorKgPerTonne: "", source: "", year: "", gwpBasis: "AR5", validFrom: "", validTo: "" };

/**
 * Company screen for supplier-specific (Method 1) waste emission factors: the factor a waste management
 * partner supplies for a waste type, optionally for one disposal method. Listing is open to any active
 * user; adding or deactivating needs the EsgAdmin or PlatformAdministrator role (enforced by the API).
 */
function WasteSupplierFactors() {
  const username = localStorage.getItem("userName");
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 768);
  const [loading, setLoading] = useState(false);
  const [factors, setFactors] = useState([]);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState("");

  const toggleSidebar = () => setIsSidebarOpen((prev) => !prev);
  const renderHeader = () =>
    localStorage.getItem("loginType") === "AAD" ? (
      <AADHeader toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} heading="" />
    ) : (
      <LoginHeader toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} heading="" />
    );

  const call = useCallback(
    async (route, { method = "GET", headers = {}, body } = {}) => {
      const response = await fetch(`${process.env.REACT_APP_BASE_API_URL}${route}`, {
        method,
        headers: { "Content-type": "application/json", username, ...headers },
        body: body ? JSON.stringify(body) : undefined,
      });
      const json = await response.json().catch(() => ({}));
      if (response.status !== 200 || (json && json.success === false)) {
        throw new Error((json && typeof json.data === "string" && json.data) || `${route} failed (${response.status})`);
      }
      return json && json.data !== undefined ? json.data : json;
    },
    [username]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await call("waste-supplier-factors", { headers: { includeinactive: String(includeInactive) } });
      setFactors(Array.isArray(list) ? list : []);
    } catch (e) {
      Swal.fire({ icon: "error", text: e.message });
    } finally {
      setLoading(false);
    }
  }, [call, includeInactive]);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async () => {
    setFormError("");
    if (!form.partnerName.trim()) return setFormError("Partner name is required.");
    if (!form.wasteType) return setFormError("Choose a waste type.");
    if (!(Number(form.factorKgPerTonne) > 0)) return setFormError("Enter a factor greater than zero (kg CO2e per tonne).");
    setLoading(true);
    try {
      await call("waste-supplier-factor", {
        method: "POST",
        body: {
          partnerName: form.partnerName.trim(),
          wasteType: form.wasteType,
          disposalMethod: form.disposalMethod,
          factorKgPerTonne: Number(form.factorKgPerTonne),
          source: form.source || null,
          year: form.year || null,
          gwpBasis: form.gwpBasis || null,
          validFrom: form.validFrom || null,
          validTo: form.validTo || null,
        },
      });
      setForm(emptyForm);
      await load();
      Swal.fire({ icon: "success", text: "Supplier factor saved. It applies to the next upload and to review confirmations." });
    } catch (e) {
      setFormError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const deactivate = async (row) => {
    const ok = await Swal.fire({ icon: "question", title: "Deactivate this factor?", text: `${row.partnerName} · ${row.wasteType} · ${row.disposalMethod === "*" ? "any method" : row.disposalMethod}. Rows already calculated with it are not changed.`, showCancelButton: true, confirmButtonText: "Deactivate", confirmButtonColor: "#B3261E" });
    if (!ok.isConfirmed) return;
    try {
      await call("waste-supplier-factor-deactivate", { method: "POST", body: { rowKey: row.rowKey } });
      await load();
    } catch (e) {
      Swal.fire({ icon: "error", text: e.message });
    }
  };

  const edit = (row) =>
    setForm({
      partnerName: row.partnerName || "",
      wasteType: row.wasteType || "",
      disposalMethod: row.disposalMethod || ANY_DISPOSAL_METHOD,
      factorKgPerTonne: row.factorKgPerTonne != null ? String(row.factorKgPerTonne) : "",
      source: row.source || "",
      year: row.year || "",
      gwpBasis: row.gwpBasis || "AR5",
      validFrom: row.validFrom ? String(row.validFrom).slice(0, 10) : "",
      validTo: row.validTo ? String(row.validTo).slice(0, 10) : "",
    });

  const dateBody = (v) => (v ? new Date(v).toLocaleDateString() : "—");

  return (
    <div className={`wrapper ${isSidebarOpen ? "" : "toggled"}`}>
      {loading && (
        <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
      {renderHeader()}
      <SideNavigation isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
      <div id="page-content-wrapper" className={isSidebarOpen ? "page-content-wrapper-pd-0" : "page-wrapper"}>
        <div className="container" style={{ paddingLeft: "30px", paddingRight: "20px" }}>
          <div className="steps__containt-hader">
            <h3 className="title mb-0">Waste supplier factors</h3>
            <div role="presentation" className="mt-2">
              <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
                <Link underline="hover" color="inherit" href="/allconfiguration" className="link">
                  Configuration
                </Link>
                <Typography color="text.primary">Waste supplier factors</Typography>
              </Breadcrumbs>
            </div>
          </div>
          <p style={{ fontSize: "13px", color: "#707070", marginTop: "8px" }}>
            Method 1 factors from your waste management partners (an EPD, LCA or certificate). When a partner has a factor for a waste type, uploads naming that partner use it instead of the DEFRA library, and rows held for review because no library factor exists can be resolved with it.
          </p>

          <div className="card border-0 shadow p-3 mt-3">
            <span style={{ fontSize: "16px", color: "#494949" }}>Add or update a factor</span>
            <div className="row g-3 mt-1">
              <div className="col-12 col-md-4">
                <Form.Label>Waste management partner *</Form.Label>
                <Form.Control value={form.partnerName} placeholder="ABC Recyclers Pvt Ltd" onChange={(e) => setForm({ ...form, partnerName: e.target.value })} />
              </div>
              <div className="col-12 col-md-4">
                <Form.Label>Waste type *</Form.Label>
                <Form.Select value={form.wasteType} onChange={(e) => setForm({ ...form, wasteType: e.target.value })}>
                  <option value="">Select…</option>
                  {WASTE_TYPES.map((t) => (
                    <option key={t}>{t}</option>
                  ))}
                </Form.Select>
              </div>
              <div className="col-12 col-md-4">
                <Form.Label>Disposal method</Form.Label>
                <Form.Select value={form.disposalMethod} onChange={(e) => setForm({ ...form, disposalMethod: e.target.value })}>
                  <option value={ANY_DISPOSAL_METHOD}>Any disposal method</option>
                  {DISPOSAL_METHODS.map((m) => (
                    <option key={m}>{m}</option>
                  ))}
                </Form.Select>
              </div>
              <div className="col-6 col-md-2">
                <Form.Label>kg CO2e / tonne *</Form.Label>
                <Form.Control type="number" min="0" step="any" value={form.factorKgPerTonne} onChange={(e) => setForm({ ...form, factorKgPerTonne: e.target.value })} />
              </div>
              <div className="col-6 col-md-4">
                <Form.Label>Source</Form.Label>
                <Form.Control value={form.source} placeholder="ABC Recyclers EPD 2026" onChange={(e) => setForm({ ...form, source: e.target.value })} />
              </div>
              <div className="col-6 col-md-2">
                <Form.Label>Year</Form.Label>
                <Form.Control value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} />
              </div>
              <div className="col-6 col-md-2">
                <Form.Label>GWP basis</Form.Label>
                <Form.Select value={form.gwpBasis} onChange={(e) => setForm({ ...form, gwpBasis: e.target.value })}>
                  {GWP_BASES.map((g) => (
                    <option key={g}>{g}</option>
                  ))}
                </Form.Select>
              </div>
              <div className="col-6 col-md-2">
                <Form.Label>Valid from</Form.Label>
                <Form.Control type="date" value={form.validFrom} onChange={(e) => setForm({ ...form, validFrom: e.target.value })} />
              </div>
              <div className="col-6 col-md-2">
                <Form.Label>Valid to</Form.Label>
                <Form.Control type="date" value={form.validTo} onChange={(e) => setForm({ ...form, validTo: e.target.value })} />
              </div>
              <div className="col-12 d-flex align-items-center gap-3">
                <Button variant="contained" onClick={submit} disabled={loading} sx={{ textTransform: "none", background: "#407775" }}>
                  Save factor
                </Button>
                <Button variant="text" onClick={() => setForm(emptyForm)} sx={{ textTransform: "none", color: "#707070" }}>
                  Clear
                </Button>
                {formError && <span style={{ color: "#B3261E", fontSize: "13px" }}>{formError}</span>}
              </div>
            </div>
          </div>

          <div className="card border-0 shadow p-3 mt-3 mb-5">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span style={{ fontSize: "16px", color: "#494949" }}>Factors</span>
              <Form.Check type="switch" id="show-inactive" label="Show inactive" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} />
            </div>
            <DataTable value={factors} size="small" stripedRows paginator rows={15} dataKey="rowKey" emptyMessage="No supplier factors yet.">
              <Column field="partnerName" header="Partner" sortable />
              <Column field="wasteType" header="Waste type" sortable />
              <Column header="Disposal method" body={(r) => (r.disposalMethod === "*" ? "Any" : r.disposalMethod)} />
              <Column header="kg CO2e / t" body={(r) => Number(r.factorKgPerTonne).toLocaleString(undefined, { maximumFractionDigits: 4 })} sortable sortField="factorKgPerTonne" />
              <Column field="source" header="Source" />
              <Column field="year" header="Year" />
              <Column field="gwpBasis" header="GWP" />
              <Column header="Valid" body={(r) => `${dateBody(r.validFrom)} → ${dateBody(r.validTo)}`} />
              <Column header="Created" body={(r) => `${dateBody(r.createdAt)} · ${r.createdBy || ""}`} />
              <Column header="Status" body={(r) => (r.isActive ? "Active" : <span style={{ color: "#707070" }}>Inactive</span>)} />
              <Column
                header="Actions"
                body={(r) => (
                  <div className="d-flex gap-2">
                    <Button size="small" variant="text" onClick={() => edit(r)} sx={{ textTransform: "none", color: "#407775" }}>
                      Edit
                    </Button>
                    {r.isActive && (
                      <Button size="small" variant="text" onClick={() => deactivate(r)} sx={{ textTransform: "none", color: "#B3261E" }}>
                        Deactivate
                      </Button>
                    )}
                  </div>
                )}
              />
            </DataTable>
          </div>
        </div>
      </div>
    </div>
  );
}

export default WasteSupplierFactors;
